import React, { useState, useContext } from 'react';
import {
  TextField, Button, Paper, InputAdornment, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box
} from '@mui/material';
import { AccountCircle, Subject, Message } from '@mui/icons-material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { ContextSession } from '../../../services/ContextSession';

const customTheme = (outerTheme) =>
  createTheme({
    palette: { mode: outerTheme.palette.mode },
    typography: {
      fontFamily: 'Roboto, Arial, sans-serif',
      h4: { fontWeight: '600', fontSize: '1.8rem', color: '#333' },
    },
  });

const EnviarMensajeTexto = () => {
  const [remitente, setRemitente] = useState('Comunicaciones INJUPEMP');
  const [asunto, setAsunto] = useState('');
  const [contenido, setContenido] = useState('');
  const [telefonos, setTelefonos] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const sessi = useContext(ContextSession);
  const outerTheme = useTheme();

  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogMessage('');
  };

  const handleContenidoChange = (e) => {
    if (e.target.value.length <= 160) {
      setContenido(e.target.value);
    }
  };

  const handleContenidoPaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText.length > 160) {
      const truncatedText = pastedText.slice(0, 160);
      setContenido(truncatedText);
      setDialogMessage('El mensaje pegado excedía los 160 caracteres permitidos y fue truncado a los primeros 160 caracteres.');
      setOpenDialog(true);
      e.preventDefault();
    } else {
      setContenido(pastedText);
    }
  };

  const handleTelefonosChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    let formattedValue = value.replace(/(504\d{8})(?!\n)/g, '$1\n');
    setTelefonos(formattedValue);
  };

  const handleTelefonosPaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    const cleanedText = pastedText.replace(/\D/g, '');
    const formattedText = cleanedText.replace(/(504\d{8})/g, '$1\n');
    setTelefonos(formattedText);
    e.preventDefault();
  };

  const handleEnviarMensaje = async () => {
    if (!asunto || !contenido || !telefonos.trim()) {
      setDialogMessage('Por favor, complete todos los campos del formulario.');
      setOpenDialog(true);
      return;
    }

    const numerosValidos = telefonos.split('\n')
      .map(num => num.trim())
      .filter(num => /^504\d{8}$/.test(num));

    if (numerosValidos.length === 0) {
      setDialogMessage('Por favor, ingrese al menos un número válido en el formato 504XXXXXXXX.');
      setOpenDialog(true);
      return;
    }

    const data = {
      remitente,
      asunto,
      contenido,
      usuarioCreadorId: sessi.sessionData.id,
      Numeros: numerosValidos,
    };

    try {
      const response = await fetch("https://bei360.injupemp.gob.hn:8444/api/MsgPart/enviarMensajeMovil/", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessi.sessionData.token}`
        },
        body: JSON.stringify(data)
      });

      const responseData = await response.json();

      if (response.ok) {
        setDialogMessage('Mensaje enviado con éxito');
        setRemitente('Comunicaciones INJUPEMP');
        setAsunto('');
        setContenido('');
        setTelefonos('');
      } else {
        setDialogMessage(`Error al enviar el mensaje: ${responseData.error || 'Error desconocido'}`);
      }
    } catch (error) {
      setDialogMessage(`Error de conexión: ${error.message}`);
    } finally {
      setOpenDialog(true);
    }
  };

  return (
    <ThemeProvider theme={customTheme(outerTheme)}>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Grid container spacing={2} justifyContent="center" alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <Paper elevation={6} sx={{ p: 4, borderRadius: '16px' }}>
              <Typography variant="h4" align="center" sx={{ mb: 3 }}>Enviar Mensaje de Texto</Typography>
              <TextField
                label="Remitente"
                variant="outlined"
                fullWidth
                value={remitente}
                onChange={(e) => setRemitente(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountCircle sx={{ color: 'gray' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Asunto"
                variant="outlined"
                fullWidth
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Subject sx={{ color: 'gray' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Contenido"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={contenido}
                onChange={handleContenidoChange}
                onPaste={handleContenidoPaste}
                sx={{ mb: 3 }}
                helperText={`${contenido.length}/160 caracteres`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Message sx={{ color: 'gray' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Números de Teléfono (504XXXXXXXX, uno por línea)"
                variant="outlined"
                fullWidth
                multiline
                rows={8}
                value={telefonos}
                onChange={handleTelefonosChange}
                onPaste={handleTelefonosPaste}
                sx={{ mb: 3 }}
              />
              <Box display="flex" justifyContent="center">
                <Button
                  onClick={handleEnviarMensaje}
                  variant="contained"
                  sx={{ backgroundColor: '#89CFE0', color: '#fff', py: 1.5, px: 4, ':hover': { backgroundColor: '#72D2DA' } }}
                >
                  Enviar Mensaje
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Mensaje</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default EnviarMensajeTexto;